export enum eChatEventTypes {
  OUTBOUND_DELETED = 'OUTBOUND_DELETED',
  INBOUND_DELETED = 'INBOUND_DELETED',
  NOTE = 'NOTE',
  MESSAGE = 'MESSAGE',
  FOLLOW_UP = 'FOLLOW_UP',
  MISSED_VOICE_CALL = 'MISSED_VOICE_CALL',
  MISSED_VIDEO_CALL = 'MISSED_VIDEO_CALL',
  CALENDAR_EVENT = 'CALENDAR_EVENT',
  TEMPLATE = 'TEMPLATE',
}

export enum eChatEventStatus {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  SENDING = 'SENDING',
  SENT = 'SENT',
  DELIVERY = 'DELIVERY',
  READ = 'READ',
  ERROR = 'ERROR',
  DECRYPTING = 'DECRYPTING',
}

export enum eNoteTypes {
  DEFAULT = 0,
  SOLD = 1,
  LOST = 2,
  ENDED = 3,
}

export enum eChatEventErrorCodes {
  PROVIDER_SEND_ERROR = 1001,
  ERROR_RECIPIENT_NOT_FOUND_ON_WHATSAPP = 1002,
  UNKNOWN_ERROR_FROM_PROVIDER = 1003,
  MESSAGE_NOT_SUPPORTED = 1004,
  MESSAGE_DECRYPTION_ERROR = 1005,
  MESSAGE_NOT_FOUND_ON_PROVIDER = 1006,
  MESSAGE_REJECTED_BY_PROVIDER = 1007,
  EMPTY_MESSAGE_ERROR = 1008,
  MESSAGE_WINDOW_CLOSED = 1009,
  RECIPIENT_UNAVAILABLE = 1010,
  AUTH_INVALID_TOKEN_ERROR = 1011,
  MEDIA_UPLOAD_ERROR = 1015,
  // Outbound send refusals, shared with messaging and whatsapp-web-service
  // (source of truth: messaging ErrorCodes). The send was refused before it
  // reached any provider, so nothing went out. Only 1016 means the workspace's
  // plan is paused; 1017 and 1018 are cases where the send could not be tied to
  // its workspace, which can happen to a workspace that is not paused, so they
  // must never render as a pause.
  WORKSPACE_PLAN_PAUSED = 1016,
  SENDER_WORKSPACE_MISMATCH = 1017,
  SENDER_WORKSPACE_UNRESOLVABLE = 1018,
  PINNED_MESSAGE_NOTIFICATION_NOT_SUPPORTED = 2001,
  PLACEHOLDER_MESSAGE_NOT_SUPPORTED = 2002,
  TEMPLATE_MESSAGE_NOT_SUPPORTED = 2003,
  INTERACTIVE_MESSAGE_NOT_SUPPORTED = 2004,
  LOTTIE_STICKER_NOT_SUPPORTED = 2005,
  GROUP_MENTION_IN_STATUS_NOTIFICATION_NOT_SUPPORTED = 2006,
  GROUP_INVITE_MESSAGE_NOT_SUPPORTED = 2007,
  DEVICE_SENT_MESSAGE_NOT_SUPPORTED = 2008,
  COMMUNITY_ANNOUNCEMENT_COMMENT_NOT_SUPPORTED = 2009,
  // BUG-865: the workspace's WhatsApp Web session is down (device unlinked,
  // logged out, session invalidated). NOT the same as MESSAGE_WINDOW_CLOSED
  // (1009) — WhatsApp Web has no 24h window; that is WAPI-only. 1009 stays
  // correct for Instagram/Messenger, which do have one.
  WHATSAPP_WEB_ACCOUNT_DISCONNECTED = 2010,
  // BUG-827: WhatsApp limited the line's new chats (the new-chat quota or a
  // reach-out timelock after bulk sending), so a send to a lead who never
  // wrote in fails. The session is alive: NOT 2010, and
  // re-scanning the QR fixes nothing. Number reserved in messaging ErrorCodes.
  WHATSAPP_WEB_NEW_CHAT_LIMIT_REACHED = 2011,
  // INT-1700: inbound WhatsApp Web message of a type outside 2001-2009 that the
  // pipeline cannot render. Number and name reserved in messaging ErrorCodes
  // (source of truth); the name must match MessageErrorType (INT-1691 analytics).
  OTHER_MESSAGE_TYPE_NOT_SUPPORTED = 2012,
  // Codes 3001-3051 aligned with messaging service ErrorCodes (source of truth)
  WAPI_CREDIT_CARD_EXPIRED_ERROR = 3001,
  WAPI_API_UNKNOWN_ERROR = 3002,
  WAPI_SERVICE_UNAVAILABLE_ERROR = 3003,
  WAPI_INVALID_PARAM_VALUE_ERROR = 3004,
  WAPI_INVALID_PARAMETER_ERROR = 3005,
  WAPI_ACCESS_TOKEN_ERROR = 3006,
  WAPI_PERMISSION_ERROR = 3007,
  WAPI_TEMPORARILY_BLOCKED_ERROR = 3008,
  WAPI_UNKNOWN_ERROR = 3009,
  WAPI_GENERAL_FAILURE_ERROR = 3010,
  WAPI_ACCESS_DENIED_ERROR = 3011,
  WAPI_MISSING_PARAMETER_ERROR = 3012,
  WAPI_SERVICE_UNAVAILABLE_ERROR_2 = 3013,
  WAPI_RECIPIENT_SENDER_CONFLICT_ERROR = 3014,
  WAPI_MESSAGE_UNDELIVERABLE_ERROR = 3015,
  WAPI_DISCONNECTED_ACCOUNT_ERROR = 3016,
  WAPI_DISPLAY_NAME_PENDING_ERROR = 3017,
  WAPI_BUSINESS_ISSUE_ERROR = 3018,
  WAPI_CERTIFICATE_ERROR = 3019,
  WAPI_REENGAGEMENT_REQUIRED_ERROR = 3020,
  WAPI_RATE_LIMITED_ERROR = 3021,
  WAPI_HEALTHY_ENGAGEMENT_ERROR = 3022,
  WAPI_USER_STOPPED_MARKETING_ERROR = 3023,
  // 3024: Reserved (gap matches backend ErrorCodes)
  WAPI_MEDIA_DOWNLOAD_ERROR = 3025,
  WAPI_MEDIA_UPLOAD_ERROR = 3026,
  WAPI_PAIR_RATE_LIMIT_ERROR = 3027,
  WAPI_CONCURRENCY_LIMIT_ERROR = 3028,
  WAPI_TEMPLATE_PARAM_COUNT_ERROR = 3029,
  WAPI_TEMPLATE_NOT_APPROVED_ERROR = 3030,
  WAPI_HYDRATED_TEXT_TOO_LONG_ERROR = 3031,
  WAPI_FORMAT_POLICY_VIOLATION_ERROR = 3032,
  WAPI_PARAMETER_FORMAT_MISMATCH_ERROR = 3033,
  WAPI_TEMPLATE_PAUSED_ERROR = 3034,
  WAPI_TEMPLATE_DISABLED_ERROR = 3035,
  WAPI_TEMPLATE_INVALID_PARAMS_ERROR = 3036,
  WAPI_INCOMPLETE_DEREGISTRATION_ERROR = 3037,
  WAPI_REGISTRATION_SERVER_UNAVAILABLE_ERROR = 3038,
  WAPI_DUPLICATE_LANGUAGE_CONTENT_ERROR = 3039,
  WAPI_TEMPLATE_LIMIT_EXCEEDED_ERROR = 3040,
  WAPI_CHARACTER_LIMIT_EXCEEDED_ERROR = 3041,
  WAPI_MISSING_FIELDS_ERROR = 3042,
  WAPI_HEADER_FORMAT_ERROR = 3043,
  WAPI_BODY_FORMAT_ERROR = 3044,
  WAPI_FOOTER_FORMAT_ERROR = 3045,
  WAPI_TEMPLATE_RECENTLY_DELETED_ERROR = 3046,
  // 3047: Reserved (gap matches backend ErrorCodes)
  WAPI_HELD_FOR_QUALITY_ERROR = 3048,
  WAPI_CLOUD_API_NOT_AVAILABLE_ERROR = 3049,
  WAPI_UNSUPPORTED_MESSAGE_TYPE_ERROR = 3050,
  WAPI_POSSIBLE_UNSUPPORTED_DEVICE_ERROR = 3051,
  // BUG-704: per-subtype refinement of Meta 131051 (type=unsupported). WBS
  // splits the old catch-all 3050 into: edit → 3063 (informational note), any
  // other/unrecognized minor subtype → 3064 (generic honest note). `unknown`,
  // absent, and empty-string subtypes stay on 3050. 3052-3062 taken (BUG-548),
  // 3024/3047 reserved. Source of truth: messaging ErrorCodes.
  WAPI_EDITED_MESSAGE_ERROR = 3063,
  WAPI_GENERIC_UNSUPPORTED_MESSAGE_ERROR = 3064,
  // INT-1214: session send rejected by the per-number throughput limiter
  // (INT-1205, wire code wba-10093). Retryable — the message was never sent
  // to Meta. 3065 is taken by messaging's WAPI_SCHEDULED_TEMPLATE_FAILED
  // (BUG-741, not consumed by this enum). Source of truth: messaging ErrorCodes.
  WAPI_SEND_RATE_LIMITED_ERROR = 3066,
  // INT-1011: whatsapp-business-service could not upload the attachment's
  // bytes to Meta in strict media-upload mode (wire code wba-10105). The
  // message was NOT sent; a plain resend re-uploads from scratch. Distinct
  // from WAPI_MEDIA_UPLOAD_ERROR (3026), whose copy describes Meta failing to
  // fetch our weblink. Source of truth: messaging ErrorCodes.
  WAPI_OUTBOUND_MEDIA_UPLOAD_ERROR = 3067,
  /**
   * RET-2257: los tres refusals del wallet. Fuente de verdad: `ErrorCodes.ts`
   * de messaging, que es lo que persiste `ChatEventErrors.code`.
   *
   * Sin ellos `parseErrors` cae en su `default` y devuelve `NO_ERROR`, que en
   * esta pantalla no significa «error sin nombre» sino «no hubo error»: la
   * burbuja no pinta ni la ⓘ. Un rechazo por saldo se veía como un mensaje que
   * simplemente no salió.
   */
  WAPI_WALLET_INSUFFICIENT_BALANCE_ERROR = 3068,
  WAPI_WALLET_UNAVAILABLE_ERROR = 3069,
  WAPI_WALLET_UNPRICEABLE_ERROR = 3070,
  // BUG-908 U12: a retry was claimed but chats-service could not confirm the
  // dispatch ever reached the queue. This does NOT assert non-delivery, which
  // is why it is absent from every retry allowlist and why its copy must not
  // read as a send failure -- telling the advisor to try again here is how the
  // customer gets the message twice. Source of truth: messaging ErrorCodes.
  WAPI_RETRY_UNCONFIRMED = 3072,
  // INT-1500 (contract C2). Source of truth: messaging ErrorCodes. 3074 is
  // intentionally unused: Meta 132018 keeps 3036 (wba-10039).
  // Meta's own codes, which reach the chat's Meta error notice:
  WAPI_NUMBER_NOT_REGISTERED_ERROR = 3073, // wba-10123, Meta 133010
  WAPI_MARKETING_DISABLED_ERROR = 3075, // wba-10121, Meta 131063
  // Sends we refused before they reached Meta (the notice's "ours" variant):
  WAPI_SCHEDULED_TEMPLATE_DELETED_ERROR = 3076, // wba-10122, BUG-844
  WAPI_MARKETING_BACKOFF_ERROR = 3077, // wba-10104, armed by Meta 131049
  WAPI_MARKETING_OPT_OUT_ERROR = 3078, // wba-10108, armed by Meta 131050
  WAPI_MARKETING_PAUSED_RESTRICTED_ERROR = 3079, // wba-10092
  WAPI_TEMPLATE_CREDITS_ERROR = 3080, // wba-10024, wba-10025
  WAPI_TEMPLATE_EMPTY_VARIABLES_ERROR = 3081, // wba-10053
  // BUG-1057: the workspace has no WhatsApp Business API account to send from.
  WAPI_ACCOUNT_NOT_CONNECTED_ERROR = 3082, // wba-10125
  // BUG-1104: refused before Meta. The sticker misses Meta's sticker limits,
  // or the message type cannot be sent through WhatsApp API.
  WAPI_STICKER_NOT_SENDABLE_ERROR = 3083, // wba-10126
  WAPI_MESSAGE_TYPE_NOT_SENDABLE_ERROR = 3084, // wba-10127
  // INT-1683: refused before Meta, an image WhatsApp would refuse (WebP, more
  // than 5 MB, 16-bit PNG).
  WAPI_IMAGE_NOT_SENDABLE_ERROR = 3085, // wba-10128
  // TikTok specific errors (4000-4999)
  TIKTOK_PROVIDER_REJECTED = 4001,
  TIKTOK_UNSUPPORTED_ATTACHMENT_TYPE = 4002,
  TIKTOK_IMAGE_NOT_SUPPORTED = 4003,
  TIKTOK_MESSAGE_WINDOW_RESTRICTED = 4004,
  // Meta (Messenger/Instagram) specific errors (5000-5999)
  // INT-898: Meta rejected the send because the lead has not written in more
  // than 7 days, so even the HUMAN_AGENT / template path is closed. NOT the
  // same as MESSAGE_WINDOW_CLOSED (1009) — 1009 is the standard 24h window,
  // which stays legitimate for Messenger/Instagram (templates still work
  // between 24h and 7 days). Source of truth: messaging ErrorCodes.
  META_INACTIVITY_WINDOW_EXPIRED = 5001,
  // INT-1377: the send left meta-messenger-service but Graph never confirmed
  // it -- a 5xx, a body that was not JSON, a 2xx with no message_id, or an
  // attachment that was delivered while its caption was not. Delivery is
  // genuinely unknown, which is why this is not one of the 1001/1007 send
  // failures: those mean nothing reached the lead and a retry is safe.
  // Source of truth: messaging ErrorCodes.
  META_SEND_UNCONFIRMED = 5002,
  // INT-1437: these five carry Meta's error_subcode, which only started
  // reaching the frontend when meta-messenger-service stopped reading it off a
  // PHP stack trace. Before that every #100 arrived as RECIPIENT_UNAVAILABLE
  // (1010) and told the seller the lead was gone, which it almost never was.
  // Source of truth: messaging ErrorCodes.
  META_MESSAGE_TOO_LONG = 5003,
  META_ATTACHMENT_UNSUPPORTED = 5004,
  META_ATTACHMENT_UPLOAD_FAILED = 5005,
  META_THREAD_CONTROL_LOST = 5006,
  // Meta could not download the file from our own media host. Deterministic
  // and ours to fix, so unlike 5005 the copy must not invite a retry.
  META_ATTACHMENT_FETCH_FAILED = 5007,
  // Deterministic like 5004, so the copy must not invite a retry -- and
  // unlike 5003 it must not name a number: Meta's cap varies by media type.
  META_ATTACHMENT_TOO_LARGE = 5008,
}

export enum eLeadStatus {
  NEUTRAL = 'NEUTRAL',
  URGENT = 'URGENT',
  IMPORTANT = 'IMPORTANT',
  PENDING = 'PENDING',
  STUCK = 'STUCK',
}

export enum eFunnelStatusName {
  NEUTRAL = 'NEUTRAL',
  URGENT = 'URGENT',
  IMPORTANT = 'IMPORTANT',
  PENDING = 'PENDING',
  STUCK = 'STUCK',
}

export enum eAttachmentMediaTypes {
  IMAGE = 'IMAGE',
  DOCUMENT = 'DOCUMENT',
  AUDIO = 'AUDIO',
  VIDEO = 'VIDEO',
  STICKER = 'STICKER',
  LOCATION = 'LOCATION',
  CONTACT = 'CONTACT',
  // SHARE_POST: TikTok shared video posts (link-preview card).
  // Wire-shape carries video_id / title / author_name / author_url /
  // thumbnail_url / embed_url instead of a top-level `url`.
  // See INT-929 / U3 plan.
  SHARE_POST = 'SHARE_POST',
}

export enum eMessageContentTypeLegacy {
  AUDIO = 'audio',
  DOCUMENT = 'document',
  STICKER = 'sticker',
  VIDEO = 'video',
  IMAGE = 'image',
  LOCATION = 'location',
  CONTACT = 'contact',
  TEXT = 'text',
  CALL_MISSED_VOICE = 'CALL_MISSED_VOICE',
  CALL_MISSED_VIDEO = 'CALL_MISSED_VIDEO',
}

export enum eLocalType {
  ES = 'es_MX',
  EN = 'en_US',
  PT = 'pt_BR',
}

export enum eLeadOrigin {
  ORGANIC = 'ORGANIC',
  CREATED = 'CREATED',
  UPLOADED = 'UPLOADED',
}

export enum eChannelType {
  WHATSAPP_WEB = 'WHATSAPP_WEB',
  WHATSAPP_API = 'WHATSAPP_API',
  MESSENGER = 'MESSENGER',
  INSTAGRAM = 'INSTAGRAM',
  TIKTOK = 'TIKTOK',
}

/** Channels where SmartLeadbot UI is shown (toggle, banners, chips). */
const SLB_SUPPORTED_CHANNELS: ReadonlySet<eChannelType> = new Set([
  eChannelType.WHATSAPP_API,
]);

export function isSLBSupportedChannel(channel: eChannelType | string | undefined): boolean {
  if (!channel) return false;
  return SLB_SUPPORTED_CHANNELS.has(channel as eChannelType);
}

export enum eBillingMigrationEventType {
  STARTED = 'started',
  FINISHED = 'finished',
}
